import React from 'react';

/* Cursor tilt for glass slabs. The pointer writes --rx/--ry (degrees) and
   --mx/--my (percent, for the sheen) onto the element and CSS does the rest, so
   nothing re-renders while the cursor moves. Coarse pointers, narrow viewports
   and reduced-motion never get the vars, which leaves the CSS resting pose. */

export type TiltOptions = {
    /** Peak rotation in degrees at the element's edge. */
    max?: number;
    /** translateZ applied while the pointer is over the element. */
    lift?: number;
    /** Uniform scale while the pointer is over the element. */
    scale?: number;
    /** Track the pointer position for the .ds-glass--sheen highlight. */
    sheen?: boolean;
};

const RESET_MS = 420;
const MIN_WIDTH = 720;

export function useTilt<T extends HTMLElement = HTMLElement>({
    max = 5,
    lift = 0,
    scale = 1,
    sheen = false,
}: TiltOptions = {}) {
    const ref = React.useRef<T | null>(null);
    const reset = React.useRef<number | undefined>(undefined);

    React.useEffect(() => () => window.clearTimeout(reset.current), []);

    const enabled = () =>
        typeof window !== 'undefined' &&
        window.matchMedia('(hover: hover)').matches &&
        window.matchMedia(`(min-width: ${MIN_WIDTH}px)`).matches &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const onPointerMove = (e: React.PointerEvent) => {
        const el = ref.current;
        if (!el || !enabled()) return;

        window.clearTimeout(reset.current);
        reset.current = undefined;
        el.removeAttribute('data-tilt-reset');

        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width;
        const py = (e.clientY - r.top) / r.height;

        el.style.setProperty('--ry', `${((px - 0.5) * max * 2).toFixed(2)}deg`);
        el.style.setProperty('--rx', `${(-(py - 0.5) * max * 2).toFixed(2)}deg`);
        if (lift) el.style.setProperty('--tz', `${lift}px`);
        if (scale !== 1) el.style.setProperty('--tscale', String(scale));
        if (sheen) {
            el.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`);
            el.style.setProperty('--my', `${(py * 100).toFixed(1)}%`);
        }
    };

    const onPointerLeave = () => {
        const el = ref.current;
        if (!el) return;
        el.setAttribute('data-tilt-reset', '');
        el.style.removeProperty('--rx');
        el.style.removeProperty('--ry');
        el.style.removeProperty('--tz');
        el.style.removeProperty('--tscale');
        window.clearTimeout(reset.current);
        reset.current = window.setTimeout(() => {
            el.removeAttribute('data-tilt-reset');
            el.style.removeProperty('--mx');
            el.style.removeProperty('--my');
            reset.current = undefined;
        }, RESET_MS);
    };

    return { ref, onPointerMove, onPointerLeave };
}
